import { styled as muiStyled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import { PureComponent } from 'react'

import Loading from '../Loading'

const StyledButton = muiStyled('button')(
  ({ theme, width, size, loading, buttonStyle }) => ({
    'borderRadius': '4px',
    'fontFamily': theme.typography.fontFamily,
    'minWidth': (() => {
      switch (width) {
        case 'mini':
          return '50px'
        case 'small':
          return '100px'
        case 'large':
          return '300px'
        case 'full':
          return '100%'
        case 'dynamic':
          return 'auto'
        case 'medium':
        default:
          return '150px'
      }
    })(),
    'fontSize': (() => {
      switch (size) {
        case 'mini':
          return '12px'
        case 'small':
          return '14px'
        case 'large':
          return '18px'
        case 'medium':
        default:
          return '16px'
      }
    })(),
    'padding': size === 'mini' || size === 'small' ? '0.5em' : '1em',
    'cursor': loading === 'true' ? 'default' : 'pointer',
    'border': 'none',
    'transition': buttonStyle === 'text' ? 'color' : 'background',
    'color':
      buttonStyle === 'text'
        ? theme.palette.pbxxCyan.main
        : theme.palette.common.white,
    'backgroundColor': (() => {
      switch (buttonStyle) {
        case 'primary':
          return theme.palette.pbxxCyan.main
        case 'info':
          return theme.palette.info.main
        case 'success':
          return theme.palette.success.main
        case 'warning':
          return theme.palette.warning.main
        case 'danger':
          return theme.palette.error.main
        case 'facebook':
          return theme.palette.facebook.main
        case 'text':
          return theme.palette.common.white
        default:
          return theme.palette.primary.main
      }
    })(),
    '&:active': {
      outline: 'none',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      color: buttonStyle === 'text' ? theme.palette.grey[400] : null,
      opacity: 0.75,
    },
    '&:hover': {
      backgroundColor: (() => {
        switch (buttonStyle) {
          case 'primary':
            return theme.palette.pbxxCyan.dark
          case 'info':
            return theme.palette.info.dark
          case 'success':
            return theme.palette.success.dark
          case 'warning':
            return theme.palette.warning.dark
          case 'danger':
            return theme.palette.error.dark
          case 'facebook':
            return theme.palette.facebook.dark
          case 'text':
            return theme.palette.grey[100]
          default:
            return theme.palette.primary.dark
        }
      })(),
    },
  }),
)

export class Button extends PureComponent {
  onClick(...args) {
    if (typeof this.props.onClick === 'function') {
      this.props.onClick.apply(undefined, args)
    }
  }
  render() {
    const { children, loading, size, ...rest } = this.props
    const buttonContent = loading ? (
      <Loading size={size === 'mini' ? 3 : 4} />
    ) : (
      children
    )
    return (
      <StyledButton
        loading={String(loading)}
        size={size}
        {...rest}
        onClick={this.onClick.bind(this)}
      >
        {buttonContent}
      </StyledButton>
    )
  }
}

Button.propTypes = {
  buttonStyle: PropTypes.oneOf([
    'primary',
    'info',
    'success',
    'warning',
    'danger',
    'facebook',
    'text',
  ]),
  children: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.object,
    PropTypes.element,
    PropTypes.string,
  ]),
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf(['mini', 'small', 'medium', 'large']),
  track: PropTypes.func,
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  width: PropTypes.oneOf([
    'mini',
    'small',
    'medium',
    'large',
    'dynamic',
    'full',
  ]),
}

Button.defaultProps = {
  buttonStyle: 'primary',
  size: 'medium',
  width: 'dynamic',
  type: 'button',
}

export default Button
